import { useState } from 'react';
import { generateInviteToken, generateInviteURL } from '../lib/invites';
import { sendGroupInviteSMS, sendEventInviteSMS } from '../lib/sms';
import { useAuthState } from '../hooks/useAuthState';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import '../styles/InviteFriendModal.css';

const InviteFriendModal = ({ 
  isOpen, 
  onClose, 
  targetKind, // 'group_join' or 'event_join'
  targetId, 
  targetName, // Group name or event title
  onInviteSent 
}) => {
  const { user } = useAuthState();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [inviteURL, setInviteURL] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteMethod, setInviteMethod] = useState('phone'); // 'phone' or 'email' or 'link'
  const [inviteSent, setInviteSent] = useState(false);

  const generateInvite = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      // Get user data for inviter name
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      const inviterName = userData?.firstName || userData?.lastName
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
        : 'Someone';

      const token = await generateInviteToken(
        {
          kind: targetKind,
          id: targetId,
        },
        user.uid,
        {
          oneTime: false,
          expiresIn: 7 * 24 * 60 * 60, // 7 days
          metadata: {
            inviterName,
            targetName,
          },
        }
      );

      const url = generateInviteURL(token);
      setInviteURL(url);
    } catch (err) {
      setError(err.message || 'Failed to generate invite');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSMS = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    if (!inviteURL) {
      await generateInvite();
      // Wait a moment for state to update
      setTimeout(() => handleSendSMS(), 100);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get user data for inviter name
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      const inviterName = userData?.firstName || userData?.lastName
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
        : 'Someone';

      // Format phone number (ensure it starts with +)
      let formattedPhone = phoneNumber.trim();
      if (!formattedPhone.startsWith('+')) {
        // Assume US number if no country code
        formattedPhone = '+1' + formattedPhone.replace(/\D/g, '');
      }

      if (targetKind === 'group_join') {
        await sendGroupInviteSMS(
          formattedPhone,
          inviteURL,
          targetName,
          inviterName,
          undefined, // recipientUserId - not known yet
          user.uid // senderUserId
        );
      } else if (targetKind === 'event_join') {
        await sendEventInviteSMS(
          formattedPhone,
          inviteURL,
          targetName,
          inviterName,
          undefined, // recipientUserId - not known yet
          user.uid // senderUserId
        );
      }

      setInviteSent(true);
      if (onInviteSent) {
        onInviteSent(inviteURL, 'sms', formattedPhone);
      }
    } catch (err) {
      setError(err.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteURL) {
      await generateInvite();
      // Wait a moment for state to update
      setTimeout(() => handleCopyLink(), 100);
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteURL);
      alert('Invite link copied to clipboard!');
      if (onInviteSent) {
        onInviteSent(inviteURL, 'link');
      }
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const handleShare = async () => {
    if (!inviteURL) {
      await generateInvite();
      // Wait a moment for state to update
      setTimeout(() => handleShare(), 100);
      return;
    }

    const shareText = `Join "${targetName}" on Hotelia! ${inviteURL}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${targetName}`,
          text: shareText,
          url: inviteURL,
        });
        if (onInviteSent) {
          onInviteSent(inviteURL, 'share');
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError('Failed to share');
        }
      }
    } else {
      // Fallback to clipboard
      handleCopyLink();
    }
  };

  const handleEmailShare = () => {
    if (!inviteURL) {
      generateInvite().then(() => {
        setTimeout(() => handleEmailShare(), 100);
      });
      return;
    }

    const subject = encodeURIComponent(`Join "${targetName}" on Hotelia`);
    const body = encodeURIComponent(`Join "${targetName}" on Hotelia!\n\n${inviteURL}`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    
    if (onInviteSent) {
      onInviteSent(inviteURL, 'email', email);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="invite-friend-modal-overlay" onClick={onClose}>
      <div className="invite-friend-modal" onClick={(e) => e.stopPropagation()}>
        <div className="invite-friend-modal-header">
          <h3>Invite Friend to {targetName}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="invite-friend-modal-content">
          {error && <div className="error-message">{error}</div>}

          {!inviteURL && (
            <div className="invite-generate-section">
              <p>Generate an invite link to share with friends</p>
              <button
                className="btn btn-primary"
                onClick={generateInvite}
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Generate Invite Link'}
              </button>
            </div>
          )}

          {inviteURL && (
            <>
              <div className="invite-method-tabs">
                <button
                  className={`tab-button ${inviteMethod === 'phone' ? 'active' : ''}`}
                  onClick={() => setInviteMethod('phone')}
                >
                  📱 SMS
                </button>
                <button
                  className={`tab-button ${inviteMethod === 'email' ? 'active' : ''}`}
                  onClick={() => setInviteMethod('email')}
                >
                  ✉️ Email
                </button>
                <button
                  className={`tab-button ${inviteMethod === 'link' ? 'active' : ''}`}
                  onClick={() => setInviteMethod('link')}
                >
                  🔗 Link
                </button>
              </div>

              {inviteMethod === 'phone' && (
                <div className="invite-method-content">
                  <label>
                    Phone Number
                    <input
                      type="tel"
                      placeholder="+1234567890"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="input"
                    />
                  </label>
                  <button
                    className="btn btn-primary"
                    onClick={handleSendSMS}
                    disabled={loading || !phoneNumber.trim()}
                  >
                    {loading ? 'Sending...' : 'Send SMS'}
                  </button>
                  {inviteSent && (
                    <p className="success-message">✅ SMS sent successfully!</p>
                  )}
                </div>
              )}

              {inviteMethod === 'email' && (
                <div className="invite-method-content">
                  <label>
                    Email Address
                    <input
                      type="email"
                      placeholder="friend@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input"
                    />
                  </label>
                  <button
                    className="btn btn-primary"
                    onClick={handleEmailShare}
                    disabled={!email.trim()}
                  >
                    Open Email
                  </button>
                </div>
              )}

              {inviteMethod === 'link' && (
                <div className="invite-method-content">
                  <label>
                    Invite Link
                    <div className="invite-url-display">
                      <input
                        type="text"
                        readOnly
                        value={inviteURL}
                        className="invite-url-input"
                      />
                      <button className="btn btn-secondary" onClick={handleCopyLink}>
                        Copy
                      </button>
                    </div>
                  </label>
                  <button
                    className="btn btn-primary"
                    onClick={handleShare}
                  >
                    Share
                  </button>
                  <p className="invite-hint">
                    This link will expire in 7 days. Share via SMS, email, or social media.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteFriendModal;
